import { useEffect, useState } from "react";
import { PuzzlePiece, CaretDown, CaretRight, GearSix, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";

export const ModulesDialog = ({ clientId, client }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [registry, setRegistry] = useState([]);
  const [fullRegistry, setFullRegistry] = useState([]);
  const [disabledDefaults, setDisabledDefaults] = useState([]);
  const [selected, setSelected] = useState({});
  const [qSel, setQSel] = useState({});
  const [expanded, setExpanded] = useState({});
  const [showDefaults, setShowDefaults] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/modules").then((r) => setRegistry(r.data)).catch((e) => toast.error(formatApiError(e)));
    if (isAdmin) {
      api.get("/modules", { params: { all: true } }).then((r) => setFullRegistry(r.data)).catch(() => {});
      api.get("/settings/module-questions").then((r) => setDisabledDefaults(r.data.disabled || [])).catch(() => {});
    }
    const sel = {};
    const q = {};
    for (const [k, v] of Object.entries(client?.modules || {})) {
      if (v?.active) sel[k] = true;
      if (v?.questions) q[k] = v.questions;
    }
    setSelected(sel);
    setQSel(q);
  }, [open, client, isAdmin]);

  const activeCount = Object.values(client?.modules || {}).filter((m) => m?.active).length;

  const selectedTaskCodes = new Set(
    ((client?.forms?.careplan?.tasks) || [])
      .filter((t) => t && (t.selected || t.levels || t.frequency || t.instruction))
      .map((t) => String(t.code))
  );

  const toggleModule = (key, on, questions) => {
    setSelected((s) => ({ ...s, [key]: on }));
    if (on && !qSel[key]) {
      const all = {};
      for (const q of questions) all[q.id] = true;
      setQSel((s) => ({ ...s, [key]: all }));
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      for (const m of registry) {
        payload[m.key] = { active: !!selected[m.key], questions: qSel[m.key] || {} };
      }
      await api.put(`/clients/${clientId}/modules`, { modules: payload });
      toast.success("Modules updated");
      setOpen(false);
      window.location.reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveDefaults = async () => {
    setBusy(true);
    try {
      await api.put("/settings/module-questions", { disabled: disabledDefaults });
      toast.success("Agency defaults saved");
      const { data } = await api.get("/modules");
      setRegistry(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleDefault = (qid, enabled) => {
    setDisabledDefaults((d) => enabled ? d.filter((x) => x !== qid) : [...d, qid]);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="modules-dialog-button">
          <PuzzlePiece size={16} className="mr-1.5" /> Modules{activeCount > 0 ? ` (${activeCount})` : ""}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="modules-dialog">
        <DialogHeader>
          <DialogTitle>Documentation modules</DialogTitle>
          <DialogDescription>
            Activate only what this client needs. Modules marked "suggested" are triggered by task codes on the care plan — the final call is always yours. Each module's documentation items can be toggled individually.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2" data-testid="modules-list">
          {registry.map((m) => {
            const state = (client?.modules || {})[m.key];
            const on = !!selected[m.key];
            const qs = m.questions || [];
            const suggested = !on && (m.task_codes || []).some((c) => selectedTaskCodes.has(String(c)));
            const isExpanded = !!expanded[m.key];
            const qState = qSel[m.key] || {};
            const enabledCount = qs.filter((q) => qState[q.id] !== false).length;
            return (
              <div key={m.key} className={`rounded-lg border transition-colors ${on ? "border-primary/40 bg-primary/5" : "border-stone-200 bg-white"}`}
                data-testid={`module-${m.key}`}>
                <div className="flex items-start gap-3 p-4">
                  <input type="checkbox" className="mt-1" checked={on}
                    onChange={(e) => toggleModule(m.key, e.target.checked, qs)}
                    data-testid={`module-toggle-${m.key}`} />
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 font-heading text-sm font-semibold text-stone-900">
                      {m.name}
                      {suggested && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#B7791F]/10 px-2 py-0.5 text-[10px] font-semibold text-[#8a5d14]" data-testid={`module-suggested-${m.key}`}>
                          <Sparkle size={10} weight="fill" /> Suggested by care plan
                        </span>
                      )}
                    </p>
                    <p className="mt-0.5 text-xs text-stone-500">{m.desc}</p>
                    {state?.active && state?.activated_at && (
                      <p className="mt-1 text-[10px] text-stone-400">Active since {String(state.activated_at).slice(0, 10)}</p>
                    )}
                  </div>
                  {on && qs.length > 0 && (
                    <button type="button" onClick={() => setExpanded((s) => ({ ...s, [m.key]: !isExpanded }))}
                      className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary hover:text-stone-900"
                      data-testid={`module-expand-${m.key}`}>
                      {isExpanded ? <CaretDown size={13} /> : <CaretRight size={13} />}
                      {enabledCount}/{qs.length} items
                    </button>
                  )}
                </div>
                {on && isExpanded && qs.length > 0 && (
                  <div className="space-y-1.5 border-t border-stone-200/70 px-4 py-3" data-testid={`module-questions-${m.key}`}>
                    {qs.map((q) => (
                      <label key={q.id} className="flex cursor-pointer items-start gap-2.5 text-xs text-stone-700">
                        <input type="checkbox" className="mt-0.5" checked={qState[q.id] !== false}
                          onChange={(e) => setQSel((s) => ({ ...s, [m.key]: { ...(s[m.key] || {}), [q.id]: e.target.checked } }))}
                          data-testid={`module-q-${q.id}`} />
                        <span>{q.text}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="modules-save-button">
            {busy ? "Saving..." : "Save Modules"}
          </Button>
        </div>

        {isAdmin && (
          <div className="mt-4 border-t border-stone-200 pt-4" data-testid="module-defaults-section">
            <button type="button" onClick={() => setShowDefaults(!showDefaults)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-500 hover:text-stone-900"
              data-testid="module-defaults-toggle">
              <GearSix size={14} /> Agency defaults (admin) {showDefaults ? <CaretDown size={12} /> : <CaretRight size={12} />}
            </button>
            {showDefaults && (
              <div className="mt-3 space-y-3">
                <p className="text-xs text-stone-500">Turn items off here to remove them agency-wide — they won't appear for any client.</p>
                {fullRegistry.map((m) => (m.questions || []).length > 0 && (
                  <div key={m.key}>
                    <p className="text-xs font-semibold text-stone-700">{m.name}</p>
                    <div className="mt-1 space-y-1">
                      {m.questions.map((q) => (
                        <label key={q.id} className="flex cursor-pointer items-start gap-2 text-xs text-stone-600">
                          <input type="checkbox" className="mt-0.5" checked={!disabledDefaults.includes(q.id)}
                            onChange={(e) => toggleDefault(q.id, e.target.checked)}
                            data-testid={`default-q-${q.id}`} />
                          <span className={disabledDefaults.includes(q.id) ? "line-through opacity-50" : ""}>{q.text}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="flex justify-end">
                  <Button size="sm" variant="outline" onClick={saveDefaults} disabled={busy} className="border-stone-300 text-stone-700" data-testid="module-defaults-save">
                    Save Agency Defaults
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
